import React, { FunctionComponent, useCallback, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import CurrentLocaleContext from '@modules/localization/interfaces/CurrentLocaleContext';
import localeClient from '@modules/clients/localeClient';
import { Locale } from '@rbx/intl';
import { join, LeadingSlashPath } from '@modules/utilities/utils/path';
import { getCookieValueByKey } from '@rbx/core';
import DOCS_LOCALE_COOKIE from '@modules/localization/constants/localizationCookieConstants';
import { defaultLocale } from '@modules/localization/constants/translationResourceConstants';
import {
  getSupportedPreferredLocalesOnBrowser,
  getUrlData,
  pathsWithNoLocale,
  shouldRedirectToTargetLocale,
  stringToLocale,
} from '../utils/localeUtils';
import { isLocaleSupported } from './translationResourceHelpers';

const CurrentLocaleProvider: FunctionComponent<React.PropsWithChildren> = ({ children }) => {
  const router = useRouter();
  const [targetLocale, setTargetLocale] = useState<Locale | null>(null);
  const [creatorHubLocale, setCreatorHubLocale] = useState<Locale>();

  const getCreatorHubLocale = useCallback(async () => {
    setCreatorHubLocale(await localeClient.getCreatorHubLocale());
  }, []);

  const getTargetLocale = useCallback(async (creatorHubLocaleResult: Locale | null) => {
    const cookieLocale = stringToLocale(getCookieValueByKey(DOCS_LOCALE_COOKIE.Name) ?? '');

    // This array is used to determine the highest priority locale to use if applicable.
    const localePriorityList = [
      cookieLocale,
      creatorHubLocaleResult,
      ...getSupportedPreferredLocalesOnBrowser(),
      defaultLocale, // hard coded en-US as the last fallback
    ];

    // Finds the first locale in the ordered list that is supported, ensuring it's a valid and recognized locale.
    const supportedLocale = localePriorityList.find(
      (locale) => locale && isLocaleSupported(locale),
    );

    if (supportedLocale) {
      setTargetLocale(supportedLocale);
    }
  }, []);

  const { urlLocale, subPath, isLocalePrefixed } = getUrlData(router.asPath as LeadingSlashPath);

  useEffect(() => {
    localeClient.getCreatorHubLocale().then((result) => {
      setCreatorHubLocale(result);
      getTargetLocale(result ?? null);
    });

    if (shouldRedirectToTargetLocale({ isLocalePrefixed, targetLocale, subPath })) {
      const path = join(process.env.siteBasePath, targetLocale!.toLowerCase(), subPath);
      // do not use router.replace with process.env.siteBasePath, since app is
      // at '/docs' and siteBasePath also includes '/docs' in prod (resulting in '/docs/docs/...')
      // spec: language change should replace current browser history of old locale page with new locale page
      window.location.replace(`${path}${window.location.search}`);
    }
  }, [getCreatorHubLocale, router, getTargetLocale, isLocalePrefixed, subPath, targetLocale]);

  return (
    <CurrentLocaleContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        urlLocale: urlLocale || defaultLocale,
        targetLocale: targetLocale ?? defaultLocale,
        creatorHubLocale: creatorHubLocale ?? defaultLocale,
        subPath,
        isLocalePrefixed,
      }}>
      {children}
    </CurrentLocaleContext.Provider>
  );
};

export const useCurrentLocaleProvider = () => {
  return useContext(CurrentLocaleContext);
};

export default CurrentLocaleProvider;
